import { ResponseError } from '@/services/http'
import { ElMessage } from 'element-plus'
import { Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import { i18n } from '@/i18n/index'

export interface HttpDefaultErrorHandle {
  errorCode: string | number | Array<number|string>
  handler(errorCode: number | string, msg: string): void
}

/** ret → vue-i18n 键 `httpErrors.<KEY>`，与 locales 四语 `httpErrors` 对齐 */
const ERROR_KEY_MAP_RAW = {
  BAD_REQUEST: { ret: 4001001 },
  AUTH_EXPIRED: { ret: 4001002 },
  THIS_VERSION_OF_CLIENT_IS_DEPRECATED: { ret: 4001003 },
  NEED_LOGIN: { ret: 4001004 },
  REQUEST_EXPIRED: { ret: 4001005 },
  FORBIDDEN: { ret: 4001006 },
  NVALID_TEAM_ID: { ret: 4800001 },
  ILLEGAL_PARAMETER: { ret: 4800002 },
  SERVER_BUSY: { ret: 5001002 },
  ILLEGAL_SORT_KEY: { ret: 4800003 },
  SMS_CODE_EXPIRED: { ret: 4901002 },
  SMS_CODE_ERROR: { ret: 4901003 },
  WRONG_ACCOUNT_PASSWORD: { ret: 4902005 },
  USER_UNBIND_TEAM: { ret: 4802001 },
  USER_HAD_ALREADY_JOIN_TEAM: { ret: 4802002 },
  PERMISSION_DENIED: { ret: 4802003 },
  TEAM_NOT_FOUND: { ret: 4802006 },
  TERMINAL_GROUP_NOT_FOUND: { ret: 4803001 },
  TERMINAL_NOT_FOUND: { ret: 4803002 },
  INVALID_IP_RANGE: { ret: 4801001 },
  INVALID_IP: { ret: 4801002 },
  IP_RULE_CONFLICT: { ret: 4801003 },
  GROUP_NAME_CONFLICT: { ret: 4801004 },
  OVER_IP_RULE_LENGTH_LIMIT: { ret: 4801005 },
  IP_FORMAT_ERROR: { ret: 4910004 },
  IP_RANGE_LIMIT_ERROR: { ret: 4910005 },
  IP_ALREADY_EXISTS: { ret: 4910006 },
  IP_NOT_INCLUDE_SELF: { ret: 491007 },
  IP_NOT_IN_WHITELIST_MODIFY: { ret: 4910007 },
  IP_NOT_IN_WHITELIST_DELETE: { ret: 4910008 },
  INVALID_STRATEGY: { ret: 4801006 },
  TOO_MANY_ITEMS_MAX_1500: { ret: 4309012 },
  GROUP_NOT_FOUND: { ret: 4801007 },
  INVALID_MAC_RULE: { ret: 4801008 },
  INVALID_GROUP_NAME_LENGTH: { ret: 4801012 },
  INVALID_GROUP_PATH: { ret: 4801013 },
  INVALID_GROUP_LEVEL: { ret: 4801014 },
  INVALID_COMPUTER_RULE: { ret: 4801020 },
  MAC_RULE_CONFLICT: { ret: 4801021 },
  COMPUTER_RULE_CONFLICT: { ret: 4801022 },
  RULE_NOT_FOUND: { ret: 4801023 },
  INVALID_RULE_TYPE: { ret: 4801024 },
  MAC_RULE_OVER_LENGTH_LIMIT: { ret: 4801025 },
  COMPUTER_RULE_OVER_LENGTH_LIMIT: { ret: 4801026 },
  RULE_AT_LEAST_ONE: { ret: 4801027 },
  SYSTEM_GROUP_NOT_ALLOWED_SET_RULE: { ret: 4801028 },
  NOT_TEAM_ADMIN: { ret: 4802011 },
  USER_REGISTERED: { ret: 4902001 },
  HAD_PAYING_ORDER: { ret: 4902002 },
  ACCOUNT_HAS_BEEN_DEACTIVATED: { ret: 4902006 },
  ACCOUNT_DOES_NOT_EXIST: { ret: 4902007 },
  USER_ALREADY_EXISTS: { ret: 4902008 },
  ADMIN_AMOUNT_LIMIT: { ret: 4802012 },
  CREATE_TEAM_EXCEEDED_LIMIT: { ret: 4802013 },
  TEAM_NAME_ALREADY_EXIST: { ret: 4802015 },
  ILLEGAL_AUTH_FILE: { ret: 4802019 },
  /** 超级管理员角色不允许修改 */
  SUPER_ADMIN_ROLE_NOT_ALLOWED_MODIFY: { ret: 4802026 },
  PERMISSION_PRICE_NOT_FOUND: { ret: 4903001 },
  CAL_PRICE_NOT_FOUND: { ret: 4903002 },
  RANGE_TOO_LARGE: { ret: 4806002 },
  REGISTRATION_RESTRICTED: { ret: 5902006 },
  TERMINAL_QUITED_TEAM: { ret: 4906004 },
  DEPLOY_POLICY_EMPTY: { ret: 4309002 },
  POLICY_NAME_EXISTED: { ret: 4309001 },
  STRATEGY_DELETED: { ret: 4309003 },
  ORDER_STATUS_NOT_PAYING: { ret: 4902003 },
  APPLY_CODE_AUTH_EXPIRED: { ret: 4841011 },
  INVALID_PASSWORD_ERROR: { ret: 4902010 },
  PASSWORD_ERROR_LIMIT: { ret: 4902013 },
  SMS_FREQUENT: { ret: 4902014 },
  GENERATE_CAPTCHA_LIMIT: { ret: 4912003 },
  HUMAN_NEED_VERIFY: { ret: 4001007 },
  HUMAN_VERIFY_ERROR: { ret: 4912002 },
  ROLE_BIND_ADMIN: { ret: 4813002 },
  MAX_COUNT_SERVICES: { ret: 4907003 },
  BALANCE_INSUFFICIENT: { ret: 4907002 },
  CASCADE_APPLY_CODE_EXPIRED: { ret: 4921001 },
  CASCADE_SUPERIOR_IP_PORT_ERROR: { ret: 4921009 },
  /** 策略/名单等批量导入条数超过服务端上限 */
  STRATEGY_IMPORT_EXCEED_LIMIT: { ret: 309012 },
  CUSTOM_SOFTWARE_NAME_EMPTY: { ret: 806004 },
  CUSTOM_SOFTWARE_PATH_EMPTY: { ret: 806005 },
  CUSTOM_SOFTWARE_BIZ_ID_EMPTY: { ret: 806006 },
  CUSTOM_SOFTWARE_NOT_FOUND: { ret: 806007 },
  CUSTOM_SOFTWARE_NAME_EXISTS: { ret: 806008 },
  FILE_UPLOAD_RETRY: { ret: 1000001 },
  FILE_UPLOAD_DUPLICATE_NAME: { ret: 4799005 },
  FILE_UPLOAD_EXCEEDS_STORAGE: { ret: 4799007 },
  FILE_UPLOAD_FILE_TOO_LARGE: { ret: 4799002 },
  FILE_UPLOAD_READ_FAILED: { ret: 4799003 },
  FILE_UPLOAD_SERVER_ERROR: { ret: 5001001 },
  INCONSISTENT_PASSWORD_ERROR: { ret: 4902009 },
  MFA_VERIFY_LIMIT_ERROR: { ret: 4902016 },
  MFA_ALREADY_BOUND: { ret: 4902017 },
  MFA_NOT_ENABLED: { ret: 4902018 },
  MFA_CODE_ERROR: { ret: 4902019 },
  MFA_TOKEN_EXPIRED: { ret: 4902020 },
  MFA_BIND_TOKEN_EXPIRED: { ret: 4902021 },
  EMAIL_FORMAT_INVALID: { ret: 4902023 },
  RESET_LINK_EXPIRED: { ret: 4902024 },
  PASSWORD_TOO_SHORT: { ret: 4902025 },
  /** mfa recovery codes not issued：尚未发行恢复验证码 */
  RECOVERY_CODE_NOT_ISSUED: { ret: 4902026 },
  /** mfa recovery code invalid or used：恢复验证码无效或已被使用 */
  RECOVERY_CODE_INVALID_OR_USED: { ret: 4902027 },
  /** mfa recovery code locked：连续失败次数达上限，已锁定 */
  RECOVERY_CODE_LOCKED: { ret: 4902028 },
  /** mfa recovery double verify failed：双重验证失败（邮箱与注册邮箱不一致） */
  RECOVERY_DOUBLE_VERIFY_FAILED: { ret: 4902029 },
  /** 邮箱已经被占用 */
  EMAIL_ALREADY_OCCUPIED: { ret: 4902030 },
  COMPANY_ID_NOT_EXIST: { ret: 4902032 },

} as const

type ErrorMapKey = keyof typeof ERROR_KEY_MAP_RAW

export type ErrorKeyMapEntry = { ret: number; msgKey: string }

export const errorKeyMap = Object.fromEntries(
  (Object.entries(ERROR_KEY_MAP_RAW) as [ErrorMapKey, { ret: number }][])
    .map(([key, { ret }]) => [key, { ret, msgKey: `httpErrors.${key}` }])
) as Record<ErrorMapKey, ErrorKeyMapEntry>

export const errorCodeMap = Object.fromEntries(
  (Object.entries(errorKeyMap) as [ErrorMapKey, ErrorKeyMapEntry][])
    .map(([, v]) => [v.ret, { key: v.msgKey.split('.').pop() as string, msgKey: v.msgKey }])
) as Record<number, { key: string; msgKey: string }>

export const errorCodeList: number[] = (Object.values(errorKeyMap) as ErrorKeyMapEntry[]).map(v => v.ret)

/** 将已知 ret 转为当前语言的提示文案；未知 ret 回退服务端 msg */
export function resolveHttpErrorMessage (ret: number, serverMsg?: string): string {
  const entry = errorCodeMap[ret]
  if (entry?.msgKey) {
    const translated = i18n.global.t(entry.msgKey)
    if (translated !== entry.msgKey) {
      return translated
    }
  }
  return (serverMsg && String(serverMsg).trim()) || ''
}

export const defaultErrorAction = function (error: ResponseError) {
  const ret = error.responseBody?.ret as number | undefined
  const serverMsg = error.responseBody?.msg as string | undefined
  const msg = ret != null ? resolveHttpErrorMessage(ret, serverMsg) : (serverMsg || '')
  if (msg) {
    ElMessage.error(msg)
  }
}

export function toastError (error: ResponseError, msg?: string) {
  if (errorCodeList.includes(error?.responseBody?.ret)) {
    defaultErrorAction(error)
  } else if (msg) {
    ElMessage.error(msg)
  }
}

// eslint-disable-next-line sonarjs/no-unused-collection
const collapseMessageList = new Set()
const collapseMessageSubject = new Subject()
// eslint-disable-next-line no-magic-numbers
collapseMessageSubject.asObservable().pipe(debounceTime(200)).subscribe((messageList: any) => {
  messageList.forEach((itemMessage: string) => {
    ElMessage.error(itemMessage)
  })
  messageList.clear()
})
export function collapseErrorMessage (msg: string) {
  collapseMessageList.add(msg)
  collapseMessageSubject.next(collapseMessageList)
}
